import { CheckExportBtn } from '@/components/CheckExportBtn';
import { handleExport, REPORT_EXPORT_COLS } from '@/lib/exportCsv';
import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ExportButtons } from '@/components/ExportButtons';import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Plus, Search, DollarSign, Clock, CheckCircle, AlertTriangle, CreditCard, Trash2, Eye, RotateCcw, Banknote, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PickFromBankDialog from '@/components/banking/PickFromBankDialog';
import { FinancialSummaryHeader } from '@/components/FinancialSummaryHeader';
import { useBranchStore } from '@/stores';

const STATUS_COLORS = {
  unpaid: 'bg-error/10 text-error border-error/30',
  partial: 'bg-warning/10 text-warning border-warning/30',
  paid: 'bg-success/10 text-success border-success/30',
};

const PAYMENT_TERMS = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_60', label: 'Net 60' },
  { value: 'net_90', label: 'Net 90' },
];

export default function BillsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bills, setBills] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  // Shared Branch+Date filter (see InvoicesPage.jsx / SupplierReportPage.jsx for the
  // same pattern): branchFilter is an array of branch ids ([] = all branches).
  const [branchFilter, setBranchFilter] = useState(searchParams.get('branch') ? [searchParams.get('branch')] : []);
  const [dateRange, setDateRange] = useState(null); // { start, end } | null, from DateQuickFilter
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [payBill, setPayBill] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, method: 'cash', reference: '' });
  // iter442e — bank-statement picker for the Record Payment modal
  const [showPickBank, setShowPickBank] = useState(false);
  const [pickedBankTxn, setPickedBankTxn] = useState(null);
  const [form, setForm] = useState({
    supplier_id: '', supplier_name: '', items: [{ description: '', quantity: 1, unit_price: 0 }],
    tax_rate: 15, vat_inclusive: false, discount: 0, due_date: '', payment_terms: 'net_30', notes: '', category: '', currency: 'SAR',
  });

  const fetchBills = async () => {
    try {
      const params = new URLSearchParams({ page, limit: pageSize });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      // branch_id for a single selection, branch_ids (CSV) for 2+ — mirrors
      // SupplierReportPage.jsx's split and the backend's precedence rule.
      if (branchFilter.length === 1) params.append('branch_id', branchFilter[0]);
      else if (branchFilter.length > 1) params.append('branch_ids', branchFilter.join(','));
      if (dateRange?.start) params.append('start_date', dateRange.start);
      if (dateRange?.end) params.append('end_date', dateRange.end);
      const res = await api.get(`/accounting/bills?${params}`);
      setBills(res.data.bills);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error('Failed to load bills'); }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers');
      setSuppliers(Array.isArray(res.data) ? res.data : res.data.suppliers || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchBills(); fetchSuppliers(); fetchBranches(); }, []);
  useEffect(() => { fetchBills(); }, [page, statusFilter, branchFilter, dateRange, pageSize]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: field === 'description' ? val : parseFloat(val) || 0 };
    setForm({ ...form, items });
  };

  const subtotal = form.items.reduce((s, it) => s + it.quantity * it.unit_price, 0);
  const taxAmount = form.vat_inclusive 
    ? Math.round((subtotal - subtotal / (1 + form.tax_rate / 100)) * 100) / 100
    : Math.round(subtotal * form.tax_rate / 100 * 100) / 100;
  const netAmount = form.vat_inclusive ? Math.round((subtotal - taxAmount) * 100) / 100 : subtotal;
  const billTotal = form.vat_inclusive ? subtotal - form.discount : subtotal + taxAmount - form.discount;

  const handleSave = async () => {
    if (!form.supplier_name) { toast.error('Supplier is required'); return; }
    if (form.items.length === 0 || !form.items[0].description) { toast.error('At least one item is required'); return; }
    try {
      await api.post('/accounting/bills', {
        ...form,
        issue_date: new Date().toISOString(),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : new Date(Date.now() + 30 * 86400000).toISOString(),
      });
      toast.success('Bill created');
      setShowModal(false);
      fetchBills();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create bill'); }
  };

  const handlePayment = async () => {
    if (payForm.amount <= 0) { toast.error('Amount must be positive'); return; }
    try {
      // iter442e — if a bank-statement txn is attached, link it AFTER
      // the regular payment posts. The /payment endpoint creates the
      // supplier_payments + journal entries; we then call the bank-
      // statement link endpoint to mark the txn as matched.
      const res = await api.post(`/accounting/bills/${payBill.id}/payment`, payForm);
      if (pickedBankTxn && payForm.method === 'bank') {
        try {
          await api.post('/banking/statement-txn/link-existing', {
            statement_id: pickedBankTxn.statement_id,
            txn_index: pickedBankTxn.txn_index,
            payment_id: res.data?.payment_id || res.data?.id,
            payee_type: 'supplier',
            payee_id: payBill.supplier_id,
          }).catch(() => { /* non-blocking; payment still recorded */ });
        } catch { /* ignore */ }
      }
      toast.success(pickedBankTxn ? 'Payment recorded & linked to bank statement' : 'Payment recorded');
      setShowPayModal(false);
      setPayBill(null);
      setPickedBankTxn(null);
      fetchBills();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bill?')) return;
    try {
      await api.delete(`/accounting/bills/${id}`);
      toast.success('Bill deleted');
      fetchBills();
    } catch { toast.error('Failed to delete'); }
  };

  const openNewBill = () => {
    setForm({
      supplier_id: '', supplier_name: '', items: [{ description: '', quantity: 1, unit_price: 0 }],
      tax_rate: 15, vat_inclusive: false, discount: 0, due_date: '', payment_terms: 'net_30', notes: '', category: '', currency: 'SAR',
    });
    setShowModal(true);
  };

  const openPay = (bill) => {
    setPayBill(bill);
    setPayForm({ amount: bill.balance_due, method: 'cash', reference: '' });
    setShowPayModal(true);
  };

  const summaryStats = {
    total: bills.length,
    unpaid: bills.filter(b => b.status === 'unpaid').reduce((s, b) => s + b.balance_due, 0),
    partial: bills.filter(b => b.status === 'partial').reduce((s, b) => s + b.balance_due, 0),
    paid: bills.filter(b => b.status === 'paid').reduce((s, b) => s + b.total, 0),
    overdue: bills.filter(b => b.status !== 'paid' && b.due_date && new Date(b.due_date) < new Date()).length,
  };

  const filteredBills = bills.filter(b =>
    !search || b.supplier_name?.toLowerCase().includes(search.toLowerCase()) || b.bill_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="bills-page">
        {/* FinancialSummaryHeader only accepts a single branchId; with a multi-branch
            selection we pass the first selected branch (known limitation — not upgrading
            this shared component here). */}
        <FinancialSummaryHeader branchId={branchFilter[0] || null} />
        <ReportHeader icon={FileText} title={<span data-testid="page-title">{t('bills_title') || 'Bills'}</span>} testId="bills-header"
          subtitle={t('bills_subtitle') || 'Manage supplier bills and payments'}
          actions={<>
            <CheckExportBtn
              disabled={!filteredBills.length}
              testId="check-export-bills-page"
              title="Validate bill rows, then export CSV"
              onClick={() => handleExport(
                filteredBills.map((b) => ({ counterparty: b.supplier_name || '', date: b.due_date || b.date, amount: b.total ?? b.amount ?? b.total_amount ?? 0, status: b.status || b.notes || '' })),
                'bills_report.csv', REPORT_EXPORT_COLS, 'bills',
              )}
            />
            <Button onClick={openNewBill} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl" data-testid="add-bill-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Bill
            </Button>
            <Link to="/bill-returns" data-testid="bills-returns-link">
              <Button variant="outline" size="sm" className="rounded-xl" title="Record supplier returns / credit notes">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Returns
              </Button>
            </Link>
          </>} />
        <div className="flex flex-wrap items-center gap-2" data-testid="bills-filter">
          <DateQuickFilter onFilterChange={setDateRange} />
          <BranchMultiSelect value={branchFilter} onChange={setBranchFilter} branches={branches} testid="bills-branch-filter" />
          <ExportButtons branchIds={branchFilter} dataType="bills" label="Bills" startDate={dateRange?.start} endDate={dateRange?.end} />
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="rounded-xl border border-error/30 bg-error/10 p-4" data-testid="summary-unpaid">
            <p className="text-xs font-medium text-error uppercase">Unpaid</p>
            <p className="text-xl font-bold text-error mt-1">{cur()} {summaryStats.unpaid.toLocaleString()}</p>
          </div>
          <div className="rounded-xl border border-warning/30 bg-warning/10 p-4" data-testid="summary-partial">
            <p className="text-xs font-medium text-warning uppercase">Partially Paid</p>
            <p className="text-xl font-bold text-warning mt-1">{cur()} {summaryStats.partial.toLocaleString()}</p>
          </div>
          <div className="rounded-xl border border-success/30 bg-success/10 p-4" data-testid="summary-paid">
            <p className="text-xs font-medium text-success uppercase">Paid</p>
            <p className="text-xl font-bold text-success mt-1">{cur()} {summaryStats.paid.toLocaleString()}</p>
          </div>
          <div className="rounded-xl border border-border bg-muted/50 p-4" data-testid="summary-overdue">
            <p className="text-xs font-medium text-muted-foreground uppercase">Overdue</p>
            <p className="text-xl font-bold text-foreground mt-1">{summaryStats.overdue} bills</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-border p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by supplier or bill number..." value={search}
                onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-bills" />
            </div>
            <div className="flex gap-2">
              {['all', 'unpaid', 'partial', 'paid'].map(s => (
                <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${statusFilter === s ? 'bg-muted text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                  data-testid={`filter-${s}`}>{s}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Bills Table */}
        <div className="bg-white rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Bill #</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Supplier</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Issue Date</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Due Date</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Balance</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBills.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No bills found. Create your first bill.</td></tr>
                ) : filteredBills.map(bill => {
                  const isOverdue = bill.status !== 'paid' && bill.due_date && new Date(bill.due_date) < new Date();
                  return (
                    <tr key={bill.id} className="border-t border-stone-50 hover:bg-muted/50 transition-colors" data-testid={`bill-row-${bill.id}`}>
                      <td className="px-4 py-3 font-mono text-muted-foreground">{bill.bill_number}</td>
                      <td className="px-4 py-3 font-medium text-foreground">{bill.supplier_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{bill.issue_date ? new Date(bill.issue_date).toLocaleDateString() : '-'}</td>
                      <td className={`px-4 py-3 ${isOverdue ? 'text-error font-medium' : 'text-muted-foreground'}`}>
                        {bill.due_date ? new Date(bill.due_date).toLocaleDateString() : '-'}
                        {isOverdue && <AlertTriangle className="inline w-3.5 h-3.5 ml-1" />}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{bill.currency || 'SAR'} {bill.total?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-medium text-error">{bill.balance_due > 0 ? `${bill.currency || 'SAR'} ${bill.balance_due.toLocaleString()}` : '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${STATUS_COLORS[bill.status] || ''}`}>{bill.status}</span>
                        {bill.zatca_phase2_signed && (
                          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400" title={`Signed ${(bill.zatca_phase2_signed_at || '').slice(0, 16).replace('T', ' ')}`} data-testid={`bill-zatca-${bill.id}`}>
                            Phase 2
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setShowDetail(bill)} className="p-1.5 rounded-md hover:bg-muted" data-testid={`view-bill-${bill.id}`}>
                            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                          {bill.status !== 'paid' && (
                            <button onClick={() => openPay(bill)} className="p-1.5 rounded-md hover:bg-success/10" data-testid={`pay-bill-${bill.id}`}>
                              <DollarSign className="w-3.5 h-3.5 text-success" />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/bill-returns?bill_id=${bill.id}&supplier_id=${bill.supplier_id || ''}`)}
                            className="p-1.5 rounded-md hover:bg-rose-100"
                            data-testid={`return-bill-${bill.id}`}
                            title="Record return / credit note for this bill"
                          >
                            <RotateCcw className="w-3.5 h-3.5 text-rose-500" />
                          </button>
                          <button onClick={() => handleDelete(bill.id)} className="p-1.5 rounded-md hover:bg-error/10" data-testid={`delete-bill-${bill.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-2 py-3 border-t border-border px-3 flex-wrap" data-testid="bills-pagination">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{total.toLocaleString()} total bills</span>
              <span className="text-xs text-muted-foreground">Page size</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="h-8 rounded-md border border-border bg-background text-xs px-2"
                data-testid="bills-page-size"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            {pages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="outline" disabled={page <= 1} className="h-8 w-8 p-0"
                onClick={() => setPage(page - 1)} data-testid="bills-prev-page"
              ><ChevronLeft size={14} /></Button>
              {(() => {
                const list = [];
                const maxVisible = 5;
                let start = Math.max(1, page - Math.floor(maxVisible / 2));
                let end = Math.min(pages, start + maxVisible - 1);
                if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                if (start > 1) { list.push(1); if (start > 2) list.push('...'); }
                for (let i = start; i <= end; i++) list.push(i);
                if (end < pages) { if (end < pages - 1) list.push('...'); list.push(pages); }
                return list.map((p, idx) => p === '...' ? (
                  <span key={`dots-${idx}`} className="text-xs text-muted-foreground px-1">...</span>
                ) : (
                  <Button
                    key={p} size="sm" variant={p === page ? 'default' : 'outline'} className="h-8 w-8 p-0 text-xs"
                    onClick={() => setPage(p)} data-testid={`bills-page-${p}`}
                  >{p}</Button>
                ));
              })()}
              <Button
                size="sm" variant="outline" disabled={page >= pages} className="h-8 w-8 p-0"
                onClick={() => setPage(page + 1)} data-testid="bills-next-page"
              ><ChevronRight size={14} /></Button>
            </div>
            )}
          </div>
        </div>

        {/* New Bill Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl" data-testid="bill-modal">
            <DialogHeader><DialogTitle>New Bill</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2 max-h-[60dvh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Supplier *</label>
                  <Select value={form.supplier_id} onValueChange={v => {
                    const sup = suppliers.find(s => s.id === v);
                    setForm({...form, supplier_id: v, supplier_name: sup?.name || ''});
                  }}>
                    <SelectTrigger data-testid="supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Currency</label>
                  <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}>
                    <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['SAR', 'AED', 'KWD', 'BHD', 'OMR', 'QAR', 'USD', 'EUR'].map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Due Date</label>
                  <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="due-date-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Payment Terms</label>
                  <Select value={form.payment_terms} onValueChange={v => setForm({...form, payment_terms: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map(pt => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">VAT Rate (%)</label>
                  <Input type="number" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: parseFloat(e.target.value) || 0})} data-testid="tax-rate-input" />
                </div>
              </div>
              
              {/* VAT Inclusive/Exclusive Toggle */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
                <span className="text-sm font-medium text-muted-foreground">VAT Mode:</span>
                <select value={form.vat_inclusive ? 'inclusive' : 'exclusive'} onChange={e => setForm({...form, vat_inclusive: e.target.value === 'inclusive'})} className="text-sm border rounded px-2 py-1.5" data-testid="bill-vat-mode">
                  <option value="exclusive">Line items exclude VAT (add on top)</option>
                  <option value="inclusive">Line items include VAT (back-calculate)</option>
                </select>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-muted-foreground">Line Items</label>
                  <Button size="sm" variant="outline" onClick={addItem} data-testid="add-line-item"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
                </div>
                <div className="space-y-2">
                  {form.items.map((item, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-5" placeholder="Description" value={item.description}
                        onChange={e => updateItem(i, 'description', e.target.value)} data-testid={`item-desc-${i}`} />
                      <Input className="col-span-2" type="number" placeholder="Qty" value={item.quantity}
                        onChange={e => updateItem(i, 'quantity', e.target.value)} data-testid={`item-qty-${i}`} />
                      <Input className="col-span-3" type="number" placeholder="Price" value={item.unit_price}
                        onChange={e => updateItem(i, 'unit_price', e.target.value)} data-testid={`item-price-${i}`} />
                      <div className="col-span-1 text-right text-sm font-medium">{(item.quantity * item.unit_price).toFixed(2)}</div>
                      <button className="col-span-1" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4 text-red-400" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                {form.vat_inclusive ? (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">Line Items Total (incl. VAT)</span><span className="font-medium">{form.currency} {subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Net Amount</span><span className="font-medium">{form.currency} {netAmount.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-info">VAT ({form.tax_rate}%) included</span><span className="font-medium text-info">{form.currency} {taxAmount.toFixed(2)}</span></div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">{form.currency} {subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-info">VAT ({form.tax_rate}%)</span><span className="font-medium text-info">+ {form.currency} {taxAmount.toFixed(2)}</span></div>
                  </>
                )}
                {form.discount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span className="font-medium text-success">-{form.currency} {form.discount.toFixed(2)}</span></div>}
                <div className="flex justify-between border-t border-border pt-2"><span className="font-bold">Total</span><span className="font-bold text-lg">{form.currency} {billTotal.toFixed(2)}</span></div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Notes</label>
                <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" data-testid="notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-bill-btn">Create Bill</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Modal */}
        <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
          <DialogContent data-testid="payment-modal">
            <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
            {payBill && (
              <div className="space-y-4 py-2">
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p className="font-medium">{payBill.supplier_name} - {payBill.bill_number}</p>
                  <p className="text-muted-foreground">Balance: {payBill.currency || 'SAR'} {payBill.balance_due?.toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Amount</label>
                  <Input type="number" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: parseFloat(e.target.value) || 0})} data-testid="payment-amount" />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Method</label>
                  <Select value={payForm.method} onValueChange={v => setPayForm({...payForm, method: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Reference</label>
                  <Input value={payForm.reference} onChange={e => setPayForm({...payForm, reference: e.target.value})} placeholder="Payment reference" data-testid="payment-reference" />
                </div>
                {/* iter442e — Bank-statement picker (only when paying by bank) */}
                {payForm.method === 'bank' && (
                  <div>
                    {pickedBankTxn ? (
                      <div className="rounded-md border border-info/30 bg-info/5 px-2 py-1.5 text-[11px] flex items-center justify-between gap-2"
                        data-testid="bill-picked-bank-txn">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{pickedBankTxn.description}</div>
                          <div className="text-muted-foreground text-[10px]">{pickedBankTxn.date} · {pickedBankTxn.bank_name || 'Bank'} · {cur()} {pickedBankTxn.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                        </div>
                        <button type="button"
                          onClick={() => setPickedBankTxn(null)}
                          className="text-error hover:underline text-[10px]"
                          data-testid="bill-clear-bank-txn">
                          Clear
                        </button>
                      </div>
                    ) : (
                      <button type="button"
                        onClick={() => setShowPickBank(true)}
                        className="text-[11px] text-info hover:underline inline-flex items-center gap-1"
                        data-testid="bill-pick-bank-btn">
                        <Banknote size={11} /> Pick from bank statement…
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPayModal(false)}>Cancel</Button>
              <Button onClick={handlePayment} className="bg-success hover:bg-green-700 text-white" data-testid="confirm-payment-btn">Record Payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bill Detail Modal */}
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-lg" data-testid="bill-detail-modal">
            <DialogHeader><DialogTitle>Bill Details</DialogTitle></DialogHeader>
            {showDetail && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Bill #:</span> <span className="font-medium">{showDetail.bill_number}</span></div>
                  <div><span className="text-muted-foreground">Supplier:</span> <span className="font-medium">{showDetail.supplier_name}</span></div>
                  <div><span className="text-muted-foreground">Issue Date:</span> <span>{showDetail.issue_date ? new Date(showDetail.issue_date).toLocaleDateString() : '-'}</span></div>
                  <div><span className="text-muted-foreground">Due Date:</span> <span>{showDetail.due_date ? new Date(showDetail.due_date).toLocaleDateString() : '-'}</span></div>
                  <div><span className="text-muted-foreground">Status:</span> <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_COLORS[showDetail.status]}`}>{showDetail.status}</span></div>
                  <div><span className="text-muted-foreground">Currency:</span> <span>{showDetail.currency || 'SAR'}</span></div>
                </div>
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">Items</p>
                  {(showDetail.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between text-sm py-1 border-b border-stone-50">
                      <span>{it.description}</span>
                      <span>{it.quantity} x {it.unit_price} = {(it.quantity * it.unit_price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span>Subtotal</span><span>{showDetail.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>VAT ({showDetail.tax_rate}%)</span><span>{showDetail.tax_amount?.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span>{showDetail.currency || 'SAR'} {showDetail.total?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-success"><span>Paid</span><span>{showDetail.amount_paid?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-error font-bold"><span>Balance</span><span>{showDetail.balance_due?.toFixed(2)}</span></div>
                </div>
                {showDetail.payments?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Payment History</p>
                    {showDetail.payments.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1.5 border-b border-stone-50">
                        <span>{new Date(p.date).toLocaleDateString()} - {p.method}</span>
                        <span className="text-success font-medium">{(showDetail.currency || 'SAR')} {p.amount?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* iter442e — Bank-statement picker for the Record Payment modal */}
        <PickFromBankDialog
          open={showPickBank}
          onClose={() => setShowPickBank(false)}
          direction="outgoing"
          entityType={payBill?.supplier_id ? 'supplier' : null}
          entityId={payBill?.supplier_id || null}
          entityName={payBill?.supplier_name || ''}
          amountHint={payForm.amount || payBill?.balance_due || null}
          onPick={(txn) => {
            setPickedBankTxn(txn);
            setPayForm(f => ({
              ...f,
              amount: txn.amount,
              reference: f.reference || `Bank: ${txn.description.slice(0, 40)}`,
            }));
            toast.success('Bank line attached — review and Save');
          }}
        />
      </div>
    </DashboardLayout>
  );
}
